import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/app/dashboard/layout';
import { Button } from '@/components/ui/button';
import { Plus, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useOrganisation } from '@/App';
import {
  getEventTypes,
  deleteEventType,
  updateEventType,
  createEventType,
} from '@/client/sdk.gen';
import type { EventTypeResponse, EventTypeCreate } from '@/client/types.gen';
import { EventTypeCard } from '@/components/ui/event-type-card';
import { EventTypeCreateModal } from '@/components/ui/event-type-create-modal';

export default function EventTypesPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { organisationId } = useOrganisation();
  const [eventTypes, setEventTypes] = useState<EventTypeResponse[]>([]);
  const [isLoadingEventTypes, setIsLoadingEventTypes] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch event types on mount and when organisationId changes
  useEffect(() => {
    if (organisationId) {
      fetchEventTypes();
    }
  }, [organisationId]);

  // Fetch event types function
  const fetchEventTypes = async () => {
    setIsLoadingEventTypes(true);
    try {
      const response = await getEventTypes({
        query: {
          include_inactive: true,
        },
      });

      if (response.data) {
        setEventTypes(response.data.event_types);
      }
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
      toast({
        title: 'Error',
        description: 'Failed to load appointment types',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingEventTypes(false);
    }
  };

  // Handle create modal actions
  const handleCreateOnly = async (data: EventTypeCreate) => {
    try {
      await createEventType({
        body: data,
      });

      toast({
        title: 'Success',
        description: 'Appointment type created successfully',
      });

      setIsCreateModalOpen(false);
      fetchEventTypes();
    } catch (error) {
      console.error('Failed to create appointment type:', error);
      toast({
        title: 'Error',
        description: 'Failed to create appointment type',
        variant: 'destructive',
      });
    }
  };

  const handleCreateAndEdit = async (data: EventTypeCreate) => {
    try {
      const response = await createEventType({
        body: data,
      });

      if (response.data) {
        navigate(`/dashboard/event-types/edit/${response.data.id}`);
      }
    } catch (error) {
      console.error('Failed to create appointment type:', error);
      toast({
        title: 'Error',
        description: 'Failed to create appointment type',
        variant: 'destructive',
      });
    }
  };

  // Handle card actions
  const handleEditEventType = (eventType: EventTypeResponse) => {
    navigate(`/dashboard/event-types/edit/${eventType.id}`);
  };

  const handleDeleteEventType = async (eventTypeId: string) => {
    try {
      await deleteEventType({
        path: { event_type_id: eventTypeId },
      });

      toast({
        title: 'Success',
        description: 'Appointment type deleted successfully',
      });

      // Refresh event types
      fetchEventTypes();
    } catch (error) {
      console.error('Failed to delete appointment type:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete appointment type',
        variant: 'destructive',
      });
    }
  };

  const handleDuplicateEventType = async (eventType: EventTypeResponse) => {
    // Navigate to create page but with duplicate data
    navigate(`/dashboard/event-types/edit/new`, {
      state: {
        duplicate: true,
        eventTypeData: {
          ...eventType,
          name: `${eventType.name} (Copy)`,
        },
      },
    });
  };

  const handleToggleActive = async (eventTypeId: string, isActive: boolean) => {
    try {
      // Update the event type status
      await updateEventType({
        path: { event_type_id: eventTypeId },
        body: {
          is_active: isActive,
        },
      });

      toast({
        title: 'Success',
        description: `Appointment type ${
          isActive ? 'activated' : 'deactivated'
        } successfully`,
      });

      // No need to refresh - the UI already updated with the local state
    } catch (error) {
      console.error('Failed to update appointment type:', error);
      toast({
        title: 'Error',
        description: 'Failed to update appointment type',
        variant: 'destructive',
      });

      // Refresh event types to revert UI in case of error
      fetchEventTypes();
    }
  };

  return (
    <DashboardLayout
      breadcrumbs={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Appointment Types' },
      ]}
    >
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Appointment Types
            </h1>
            <p className="text-muted-foreground">
              Create and manage your appointment types
            </p>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Appointment Type
          </Button>
        </div>

        {/* Create Modal */}
        <EventTypeCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreateOnly={handleCreateOnly}
          onCreateAndEdit={handleCreateAndEdit}
        />

        {/* Appointment Types Grid */}
        {isLoadingEventTypes ? (
          <div className="rounded-md border p-8 shadow-sm flex items-center justify-center">
            <div className="flex flex-col items-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin mb-2" />
              <p className="text-muted-foreground">
                Loading appointment types...
              </p>
            </div>
          </div>
        ) : eventTypes.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {eventTypes.map((eventType) => (
              <EventTypeCard
                key={eventType.id}
                eventType={eventType}
                onEdit={handleEditEventType}
                onDelete={handleDeleteEventType}
                onDuplicate={handleDuplicateEventType}
                onToggleActive={handleToggleActive}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-md border shadow-sm">
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <h3 className="mt-2 text-lg font-semibold">
                No appointment types yet
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first appointment type to get started
              </p>
              <Button
                variant="outline"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                New Appointment Type
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
